/**
 * JC Area Intelligence — provider configuration and mode resolution.
 *
 * Provider model:
 *  - 'platform'  — always available. Renders only REAL platform data
 *                  (location records, nearby bases, curated photography).
 *                  No API key, no external calls, nothing fabricated.
 *  - 'google'    — enrichment layer (Street View, Places, Routes, Geocoding,
 *                  map canvas). Activates only when an API key is injected at
 *                  build time via REACT_APP_GOOGLE_MAPS_API_KEY. The key is
 *                  never committed; absence degrades gracefully to 'platform'.
 *
 * Design gate: the cartographic map canvas is additionally gated behind
 * REACT_APP_AREA_INTEL_MAP_CANVAS because the StayFinder design backbone's
 * no-map policy (STAYFINDER_DESIGN_BACKBONE_V1 §2) currently forbids map UI
 * on customer surfaces. Flipping that flag requires a recorded backbone
 * amendment by Jason. Street View (photographic), textual route cards, and
 * places cards are backbone-compatible and are not gated.
 */

export const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || null;

/** Design-gated: cartographic canvas requires an explicit backbone override. */
export const MAP_CANVAS_ENABLED = process.env.REACT_APP_AREA_INTEL_MAP_CANVAS === 'true';

/** Kill switch for the whole panel (defaults on). */
export const AREA_INTELLIGENCE_ENABLED = process.env.REACT_APP_AREA_INTEL_ENABLED !== 'false';

/**
 * Capabilities by provider mode. The panel renders a section only when the
 * active provider claims the capability AND real data exists for it — no
 * placeholder boxes, no fake pins, no empty cards.
 */
export function resolveAreaIntelligence({
  apiKey = GOOGLE_MAPS_API_KEY,
  mapCanvasEnabled = MAP_CANVAS_ENABLED,
  enabled = AREA_INTELLIGENCE_ENABLED,
} = {}) {
  if (!enabled) {
    return { enabled: false, mode: 'off', capabilities: {} };
  }
  if (!apiKey) {
    return {
      enabled: true,
      mode: 'platform',
      capabilities: {
        photos: true,
        bases: true,
        highlights: true,
        streetView: false,
        places: false,
        routes: false,
        mapCanvas: false,
      },
    };
  }
  return {
    enabled: true,
    mode: 'google',
    capabilities: {
      photos: true,
      bases: true,
      highlights: true,
      streetView: true,
      places: true,
      routes: true,
      // Cartographic canvas stays off until the backbone amendment is recorded.
      mapCanvas: Boolean(mapCanvasEnabled),
    },
  };
}


/**
 * Pure display-status resolver for the embeddable module — the section
 * NEVER silently disappears. 'hidden' only via the explicit kill switch.
 */
export function resolveModuleDisplayStatus({ enabled, hasCoords, mode, status }) {
  if (!enabled) return 'hidden';
  if (!hasCoords) return 'no_coords';
  if (mode !== 'google') return 'unavailable';
  if (status === 'loading') return 'loading';
  if (status === 'live') return 'live';
  return 'unavailable';
}
